'use client';

import {
  ArrowDown01Icon,
  ArrowLeftRightIcon,
  ArrowRight01Icon,
} from '@terra/ui-icons';
import { Box, HStack, VStack } from '@terra/ui-styled-system';
import { useTranslations } from 'next-intl';
import { useState } from 'react';
import { Collapsible } from '../../../components/collapsible';
import { BodyDefault, MetaDefault } from '../../../components/typography';

function parentMessagePreview(message: string): string {
  const firstLine = message.split('\n').find((line) => line.trim().length > 0);

  return (firstLine ?? message).trim();
}

export function SubagentParentMessage({ message }: { message: string }) {
  const t = useTranslations('designSystem.chat.parallelAgents');
  const [isExpanded, setIsExpanded] = useState(false);
  const preview = parentMessagePreview(message);

  return (
    <Collapsible.Root
      open={isExpanded}
      onOpenChange={({ open }) => setIsExpanded(open)}
      data-testid="subagent-parent-message"
      data-expanded={isExpanded ? 'true' : 'false'}
    >
      <VStack
        alignItems="stretch"
        gap={1}
        width="full"
        minH={0}
        maxH="full"
        px={2.5}
        py={2}
        bg="bg.default"
        borderWidth="1px"
        borderColor="border.subtle"
        borderRadius="lg"
        boxShadow="xs"
      >
        <Collapsible.Trigger asChild>
          <HStack
            gap={1.5}
            alignItems="center"
            width="full"
            minW={0}
            flexShrink={0}
            cursor="pointer"
            color="fg.muted"
            title={
              isExpanded ? t('hideParentMessage') : t('showParentMessage')
            }
            _hover={{ color: 'fg.default' }}
          >
            <ArrowLeftRightIcon size={12} />
            <MetaDefault color="inherit" flexShrink={0}>
              {t('sentByParent')}
            </MetaDefault>
            {!isExpanded ? (
              <MetaDefault color="fg.subtle" truncate minW={0}>
                {preview}
              </MetaDefault>
            ) : null}
            <Box display="flex" alignItems="center" flexShrink={0} ml="auto">
              {isExpanded ? (
                <ArrowDown01Icon size={10} />
              ) : (
                <ArrowRight01Icon size={10} />
              )}
            </Box>
          </HStack>
        </Collapsible.Trigger>
        {isExpanded ? (
          <Collapsible.Content>
            <Box maxH="4.5rem" minH={0} overflowY="auto" flex="1">
              <BodyDefault color="fg.default" whiteSpace="pre-wrap">
                {message}
              </BodyDefault>
            </Box>
          </Collapsible.Content>
        ) : null}
      </VStack>
    </Collapsible.Root>
  );
}
